import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api, { formatApiError } from "@/lib/api";
import { IDS } from "@/lib/testIds";
import { toast } from "sonner";
import { QrCode, Camera, X, MagnifyingGlass, ArrowsClockwise, Info, Play } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { isAdmin } from "@/lib/roles";

// Strategy chain — each is tried in order until one attaches a live track.
const STRATEGIES = ["device_id", "facing_ideal", "any_video"];

function isSecureContext() {
  return typeof window !== "undefined" && (window.isSecureContext || location.protocol === "https:");
}
function isInIframe() { try { return window.self !== window.top; } catch (_) { return true; } }

// Poll a <video> element and produce a shallow snapshot of its runtime state.
function snapshotVideo(v) {
  if (!v) return { found: false };
  const stream = v.srcObject;
  const tracks = (stream && stream.getVideoTracks && stream.getVideoTracks()) || [];
  const track = tracks[0];
  const settings = track && track.getSettings ? track.getSettings() : {};
  return {
    found: true,
    hasSrcObject: !!stream,
    trackCount: tracks.length,
    trackReadyState: track ? track.readyState : "—",
    trackMuted: track ? track.muted : "—",
    trackLabel: track ? track.label : "—",
    trackWidth: settings.width || "—",
    trackHeight: settings.height || "—",
    videoWidth: v.videoWidth,
    videoHeight: v.videoHeight,
    paused: v.paused,
    readyState: v.readyState,
    autoplay: v.autoplay,
    muted: v.muted,
    playsInline: !!v.playsInline,
    clientWidth: v.clientWidth,
    clientHeight: v.clientHeight,
  };
}

export default function Scan() {
  const { user } = useAuth();
  const debugAvailable = isAdmin(user);
  const [showDebug, setShowDebug] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("scan_debug") === "1"));
  // iter19: sticky Stock In / Stock Out selection. Persists across scans.
  const [action, setAction] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("scan_action")) || "add");
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [recent, setRecent] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [cameraError, setCameraError] = useState(null);
  const [permState, setPermState] = useState("unknown");
  const [diag, setDiag] = useState({ https: false, mediaDevices: false, iframe: false, deviceCount: 0 });
  const [videoSnap, setVideoSnap] = useState({ found: false });
  const [instanceCount, setInstanceCount] = useState(0);
  // Raw test state — bypasses html5-qrcode entirely.
  const [rawTest, setRawTest] = useState({ active: false, error: null, snap: null });

  const scannerRef = useRef(null);
  const rawVideoRef = useRef(null);
  const rawStreamRef = useRef(null);
  const nav = useNavigate();

  // ---- Diagnostic refresh ----
  const refreshDiagnostics = useCallback(async () => {
    const d = {
      https: isSecureContext(),
      mediaDevices: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      iframe: isInIframe(),
      deviceCount: 0,
    };
    try {
      if (d.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        const list = await navigator.mediaDevices.enumerateDevices();
        d.deviceCount = list.filter((x) => x.kind === "videoinput").length;
      }
      const cams = await Html5Qrcode.getCameras().catch(() => []);
      setCameras(cams || []);
      if (cams && cams.length > 0 && !selectedCameraId) {
        const rear = cams.find((c) => /back|rear|environment/i.test(c.label));
        setSelectedCameraId((rear || cams[cams.length - 1]).id);
      }
    } catch (_) { /* ignore */ }
    setDiag(d);
    // permissions API (may throw on Safari)
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const res = await navigator.permissions.query({ name: "camera" });
        setPermState(res.state);
      }
    } catch (_) { /* not supported */ }
  }, [selectedCameraId]);

  useEffect(() => {
    try { setRecent(JSON.parse(localStorage.getItem("recent_scans") || "[]")); } catch (_) { /* ignore */ }
    refreshDiagnostics();
    return () => {
      const inst = scannerRef.current;
      if (inst) {
        Promise.resolve(inst.stop()).then(() => inst.clear()).catch(() => {});
        scannerRef.current = null;
      }
      const rs = rawStreamRef.current;
      if (rs) rs.getTracks().forEach((t) => t.stop());
      rawStreamRef.current = null;
    };
  }, [refreshDiagnostics]);

  // Live poll of the html5-qrcode video element while scanning.
  useEffect(() => {
    if (!scanning) return;
    const iv = setInterval(() => {
      const v = document.querySelector("#scan-target video");
      if (v) {
        v.setAttribute("playsinline", "true");
        v.setAttribute("autoplay", "true");
        v.muted = true;
        if (v.paused && v.readyState >= 2) v.play().catch(() => {});
      }
      setVideoSnap(snapshotVideo(v));
    }, 500);
    return () => clearInterval(iv);
  }, [scanning]);

  // Live poll of the raw test video.
  useEffect(() => {
    if (!rawTest.active) return;
    const iv = setInterval(() => {
      setRawTest((s) => ({ ...s, snap: snapshotVideo(rawVideoRef.current) }));
    }, 500);
    return () => clearInterval(iv);
  }, [rawTest.active]);

  // ---- Helpers ----
  const pushRecent = (val) => {
    const arr = [val, ...recent.filter((r) => r !== val)].slice(0, 8);
    setRecent(arr);
    localStorage.setItem("recent_scans", JSON.stringify(arr));
  };

  const handleCode = async (value) => {
    if (!value) return;
    if (navigator.vibrate) navigator.vibrate(60);
    try {
      const { data } = await api.get(`/items/lookup/${encodeURIComponent(value)}`);
      pushRecent(value);
      // iter19: forward the sticky Stock In/Out selection so the action page can auto-select it.
      const qs = `action=${encodeURIComponent(action)}`;
      if (data.kind === "meal_record") nav(`/scan/action/${data.meal_record.meal_record_id}?${qs}`);
      else if (data.kind === "variant") nav(`/scan/dry-action/${data.variant.variant_id}?${qs}&loc=${data.locations?.[0]?.location_id || ""}`);
      else if (data.kind === "location") toast.success(`Location: ${data.location.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch (_) { /* ignore */ }
      scannerRef.current = null;
      setInstanceCount((n) => Math.max(0, n - 1));
    }
    setScanning(false);
  }, []);

  const stopRaw = useCallback(() => {
    const rs = rawStreamRef.current;
    if (rs) rs.getTracks().forEach((t) => t.stop());
    rawStreamRef.current = null;
    if (rawVideoRef.current) rawVideoRef.current.srcObject = null;
    setRawTest({ active: false, error: null, snap: null });
  }, []);

  const startScanner = useCallback(async () => {
    setCameraError(null);
    if (scannerRef.current) { await stopScanner(); }
    if (!isSecureContext()) return setCameraError({ type: "insecure", message: "Camera requires HTTPS." });
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      return setCameraError({ type: "unsupported", message: "This browser does not expose getUserMedia." });
    }
    // Make target visible BEFORE start so Android Chrome will bind the stream to a laid-out element.
    setScanning(true);
    await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 100)));

    const tryStrategies = STRATEGIES.filter((s, i, arr) => arr.indexOf(s) === i);
    let lastError = null;
    for (const strat of tryStrategies) {
      let cameraArg;
      if (strat === "device_id" && selectedCameraId) cameraArg = selectedCameraId;
      else if (strat === "facing_ideal") cameraArg = { facingMode: { ideal: "environment" } };
      else if (strat === "any_video") cameraArg = true; // any camera
      else continue;
      try {
        const inst = new Html5Qrcode("scan-target");
        scannerRef.current = inst;
        setInstanceCount((n) => n + 1);
        await inst.start(
          cameraArg,
          { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0, disableFlip: false },
          (decoded) => { stopScanner(); handleCode(decoded); },
          () => {}
        );
        // Enforce inline playback (Android Chrome/iOS Safari)
        const v = document.querySelector("#scan-target video");
        if (v) {
          v.setAttribute("playsinline", "true");
          v.setAttribute("webkit-playsinline", "true");
          v.setAttribute("autoplay", "true");
          v.muted = true;
          try { await v.play(); } catch (_) { /* auto-play policy may reject; poll loop retries */ }
        }
        await refreshDiagnostics();
        return;
      } catch (e) {
        lastError = e;
        try { if (scannerRef.current) await scannerRef.current.stop(); } catch (_) { /* ignore */ }
        scannerRef.current = null;
        setInstanceCount((n) => Math.max(0, n - 1));
      }
    }
    setScanning(false);
    const raw = (lastError && (lastError.message || lastError.name || String(lastError))) || "unknown";
    const lower = raw.toLowerCase();
    let type = "other";
    let message = "Camera could not be started. Try the Raw Camera Test below to isolate the layer.";
    if (lower.includes("notallowed") || lower.includes("denied")) { type = "denied"; message = "Camera permission denied. Enable in Chrome → Site Settings, then Retry."; }
    else if (lower.includes("notfound") || lower.includes("devicesnotfound")) { type = "notfound"; message = "No camera device found."; }
    else if (lower.includes("notreadable") || lower.includes("inuse") || lower.includes("trackstart")) { type = "inuse"; message = "Camera busy — close other camera apps."; }
    else if (lower.includes("overconstrained") || lower.includes("constraint")) { type = "constraint"; message = "Camera does not satisfy constraints — try a different camera in the picker."; }
    setCameraError({ type, message, raw });
  }, [selectedCameraId, refreshDiagnostics, stopScanner]);

  // ---- Raw camera test (bypasses html5-qrcode) ----
  const startRawTest = useCallback(async () => {
    setRawTest({ active: true, error: null, snap: null });
    try {
      const constraints = selectedCameraId
        ? { video: { deviceId: { exact: selectedCameraId } }, audio: false }
        : { video: { facingMode: { ideal: "environment" } }, audio: false };
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e1) {
        // Fallback to any video
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      rawStreamRef.current = stream;
      // Wait a tick so React renders the <video>
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 60)));
      const v = rawVideoRef.current;
      if (!v) throw new Error("raw <video> ref not mounted");
      v.setAttribute("playsinline", "true");
      v.setAttribute("webkit-playsinline", "true");
      v.setAttribute("autoplay", "true");
      v.muted = true;
      v.srcObject = stream;
      try { await v.play(); } catch (e) { /* poll will retry */ }
      setRawTest({ active: true, error: null, snap: snapshotVideo(v) });
    } catch (e) {
      const msg = (e && (e.message || e.name || String(e))) || "unknown";
      setRawTest({ active: true, error: msg, snap: null });
    }
  }, [selectedCameraId]);

  const submitManual = (e) => { e.preventDefault(); if (manual.trim()) handleCode(manual.trim()); setManual(""); };
  const retry = () => { setCameraError(null); startScanner(); };

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      <div className="mb-4">
        <div className="label-tech mb-1">Field Operations</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Scan / Search</h1>
      </div>

      {/* iter19 · Sticky Stock In / Stock Out selector */}
      <div className="card-flat border-border p-3 mb-3 flex items-center gap-3">
        <span className="label-tech shrink-0">Action</span>
        <div className="grid grid-cols-2 gap-1 flex-1">
          <button
            data-testid="scan-action-add"
            onClick={() => { setAction("add"); try { localStorage.setItem("scan_action", "add"); } catch {} }}
            className={`h-9 rounded-sm text-[11px] font-bold uppercase tracking-widest btn-hard ${action === "add" ? "bg-green-600 text-white border-green-600" : "border border-border hover:bg-muted"}`}
          >Stock In</button>
          <button
            data-testid="scan-action-take_out"
            onClick={() => { setAction("take_out"); try { localStorage.setItem("scan_action", "take_out"); } catch {} }}
            className={`h-9 rounded-sm text-[11px] font-bold uppercase tracking-widest btn-hard ${action === "take_out" ? "bg-blue-600 text-white border-blue-600" : "border border-border hover:bg-muted"}`}
          >Stock Out</button>
        </div>
      </div>

      {/* ---- Admin-only DIAGNOSTIC BLOCK ---- */}
      {debugAvailable && (
        <div className="mb-3 flex items-center justify-end">
          <label className="flex items-center gap-2 text-[10px] font-mono cursor-pointer">
            <input
              type="checkbox"
              checked={showDebug}
              onChange={(e) => {
                setShowDebug(e.target.checked);
                localStorage.setItem("scan_debug", e.target.checked ? "1" : "0");
              }}
              data-testid="scan-debug-toggle"
            />
            Show camera diagnostics (Admin debug)
          </label>
        </div>
      )}
      {debugAvailable && showDebug && (
      <div className="card-flat border-2 border-yellow-500/60 bg-yellow-500/5 p-3 mb-3 text-[11px] font-mono" data-testid="scan-diagnostics">
        <div className="flex items-center gap-2 mb-2">
          <Info size={14} weight="bold" className="text-yellow-500" />
          <span className="label-tech">Live Diagnostics</span>
          <button
            onClick={refreshDiagnostics}
            className="ml-auto btn-hard h-6 px-2 border border-border text-[9px] font-bold uppercase tracking-widest rounded-sm hover:bg-muted"
          ><ArrowsClockwise size={10} weight="bold" /></button>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
          <span>HTTPS:</span><span className={diag.https ? "text-success" : "text-destructive"}>{String(diag.https)}</span>
          <span>mediaDevices:</span><span className={diag.mediaDevices ? "text-success" : "text-destructive"}>{String(diag.mediaDevices)}</span>
          <span>in-iframe:</span><span className={diag.iframe ? "text-yellow-500" : "text-success"}>{String(diag.iframe)}</span>
          <span>permission state:</span><span>{permState}</span>
          <span>videoinput devices:</span><span>{diag.deviceCount}</span>
          <span>cameras enumerated:</span><span>{cameras.length}</span>
          <span>selected camera:</span><span className="truncate">{selectedCameraId ? selectedCameraId.slice(0, 12) + "…" : "—"}</span>
          <span>scanner instances:</span><span>{instanceCount}</span>
          <span className="col-span-2 mt-1 border-t border-yellow-500/30 pt-1 label-tech">Video element</span>
          <span>video found:</span><span className={videoSnap.found ? "text-success" : "text-destructive"}>{String(!!videoSnap.found)}</span>
          <span>srcObject:</span><span className={videoSnap.hasSrcObject ? "text-success" : "text-destructive"}>{String(!!videoSnap.hasSrcObject)}</span>
          <span>trackCount:</span><span>{videoSnap.trackCount ?? "—"}</span>
          <span>track readyState:</span><span>{videoSnap.trackReadyState}</span>
          <span>track muted:</span><span>{String(videoSnap.trackMuted)}</span>
          <span>track label:</span><span className="truncate">{(videoSnap.trackLabel || "—").toString().slice(0, 24)}</span>
          <span>track WxH:</span><span>{videoSnap.trackWidth}×{videoSnap.trackHeight}</span>
          <span>video WxH:</span><span>{videoSnap.videoWidth ?? 0}×{videoSnap.videoHeight ?? 0}</span>
          <span>client WxH:</span><span>{videoSnap.clientWidth ?? 0}×{videoSnap.clientHeight ?? 0}</span>
          <span>paused:</span><span>{String(videoSnap.paused)}</span>
          <span>readyState:</span><span>{videoSnap.readyState ?? "—"}</span>
          <span>playsInline:</span><span>{String(videoSnap.playsInline)}</span>
          <span>last error:</span><span className="text-destructive col-span-1 truncate">{cameraError?.raw || cameraError?.message || "—"}</span>
        </div>
      </div>
      )}

      {/* Camera picker */}
      {cameras.length > 0 && (
        <div className="card-flat border-border p-3 mb-3 flex items-center gap-2">
          <Camera size={14} weight="bold" className="text-muted-foreground" />
          <span className="label-tech shrink-0">Camera</span>
          <select
            data-testid="camera-picker"
            value={selectedCameraId}
            onChange={(e) => setSelectedCameraId(e.target.value)}
            disabled={scanning}
            className="flex-1 h-9 px-2 border border-border bg-background rounded-sm text-xs font-mono disabled:opacity-50"
          >
            {cameras.map((c) => (<option key={c.id} value={c.id}>{c.label || `Camera ${c.id.slice(0, 8)}`}</option>))}
          </select>
        </div>
      )}

      {/* ---- html5-qrcode scan area ---- */}
      <div className="card-flat border-border overflow-hidden mb-3">
        <div className="relative bg-black" style={{ height: 340, width: "100%" }}>
          {/* #scan-target ALWAYS in flow (no display:none, no opacity:0, no visibility:hidden) */}
          <div
            id="scan-target"
            data-testid="scan-target"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", background: "#000" }}
          />
          {!scanning && (
            <div className="absolute inset-0 grid place-items-center text-center p-6 z-10 pointer-events-none">
              <div>
                <div className="w-20 h-20 mx-auto mb-3 border-2 border-primary/60 rounded-sm grid place-items-center pulse-orange">
                  <QrCode size={36} weight="fill" className="text-primary" />
                </div>
                <div className="font-display font-black text-white text-base tracking-tight mb-1">Ready to Scan</div>
                <div className="text-white/60 text-xs max-w-xs mx-auto">Tap start to open the camera.</div>
              </div>
            </div>
          )}
          {scanning && (
            <div className="absolute inset-x-0 top-2 flex items-center justify-between px-3 z-20">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/80 font-mono">Scanning…</div>
              <button data-testid={IDS.scanStopCamera} onClick={stopScanner}
                className="w-8 h-8 grid place-items-center bg-black/60 text-white border border-white/40 rounded-sm">
                <X size={16} weight="bold" />
              </button>
            </div>
          )}
        </div>
        <div className="p-3 border-t border-border flex gap-2">
          {!scanning ? (
            <button data-testid={IDS.scanStartCamera} onClick={startScanner}
              className="btn-hard flex-1 h-11 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 flex items-center justify-center gap-2">
              <Camera size={16} weight="fill" /> {cameraError ? "Retry Camera" : "Start Camera"}
            </button>
          ) : (
            <button onClick={stopScanner}
              className="btn-hard flex-1 h-11 border border-destructive text-destructive font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-destructive hover:text-destructive-foreground">
              Stop Camera
            </button>
          )}
        </div>
      </div>

      {/* ---- Admin-only RAW CAMERA TEST ---- */}
      {debugAvailable && showDebug && (
      <div className="card-flat border-2 border-blue-500/60 bg-blue-500/5 p-3 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <Play size={14} weight="bold" className="text-blue-500" />
          <span className="label-tech">Raw Camera Test</span>
          <span className="text-[10px] font-mono text-muted-foreground">bypasses html5-qrcode</span>
          <button
            data-testid="raw-camera-test-btn"
            onClick={rawTest.active ? stopRaw : startRawTest}
            className={`ml-auto btn-hard h-8 px-3 text-[10px] font-bold uppercase tracking-widest rounded-sm ${
              rawTest.active ? "border border-destructive text-destructive" : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >{rawTest.active ? "Stop Raw" : "Open Raw Camera Test"}</button>
        </div>
        {rawTest.active && (
          <div className="mt-2">
            <div className="bg-black w-full" style={{ height: 240 }}>
              <video
                ref={rawVideoRef}
                data-testid="raw-video"
                autoPlay
                muted
                playsInline
                webkit-playsinline="true"
                style={{ width: "100%", height: "100%", objectFit: "cover", background: "#000" }}
              />
            </div>
            {rawTest.error && (
              <pre className="text-[10px] text-destructive font-mono mt-2 whitespace-pre-wrap">Error: {rawTest.error}</pre>
            )}
            {rawTest.snap && (
              <div className="text-[10px] font-mono mt-2 grid grid-cols-2 gap-x-3">
                <span>srcObject:</span><span className={rawTest.snap.hasSrcObject ? "text-success" : "text-destructive"}>{String(!!rawTest.snap.hasSrcObject)}</span>
                <span>track readyState:</span><span>{rawTest.snap.trackReadyState}</span>
                <span>track muted:</span><span>{String(rawTest.snap.trackMuted)}</span>
                <span>track label:</span><span className="truncate">{(rawTest.snap.trackLabel || "—").toString().slice(0, 32)}</span>
                <span>track WxH:</span><span>{rawTest.snap.trackWidth}×{rawTest.snap.trackHeight}</span>
                <span>video WxH:</span><span>{rawTest.snap.videoWidth}×{rawTest.snap.videoHeight}</span>
                <span>paused:</span><span>{String(rawTest.snap.paused)}</span>
                <span>readyState:</span><span>{rawTest.snap.readyState}</span>
              </div>
            )}
            <div className="text-[10px] text-muted-foreground mt-2">
              If this shows a LIVE preview: html5-qrcode integration is at fault.<br />
              If this ALSO stays black: Android/browser or CSS/overlay is at fault.<br />
              If getUserMedia rejects: permission or hardware.
            </div>
          </div>
        )}
      </div>
      )}

      {cameraError && (
        <div data-testid="camera-error-banner"
          className={`border-2 rounded-sm p-3 mb-3 flex items-start gap-3 ${
            cameraError.type === "denied" ? "border-destructive bg-destructive/10" : "border-yellow-500 bg-yellow-500/10"
          }`}>
          <div className="flex-1 text-xs leading-relaxed">
            <div className="font-bold text-[10px] uppercase tracking-widest mb-1">
              {cameraError.type === "denied" ? "🎥 Permission Denied" :
               cameraError.type === "notfound" ? "🎥 No Camera" :
               cameraError.type === "inuse" ? "🎥 Camera Busy" :
               cameraError.type === "insecure" ? "🔒 HTTPS Required" :
               cameraError.type === "constraint" ? "🎥 Constraint Failed" :
               cameraError.type === "unsupported" ? "🚫 Unsupported" : "⚠️ Camera Error"}
            </div>
            <div className="mb-2">{cameraError.message}</div>
            {cameraError.raw && <pre className="text-[10px] mt-1 opacity-80 whitespace-pre-wrap">{cameraError.raw}</pre>}
            <button data-testid="camera-retry-btn" onClick={retry}
              className="btn-hard h-8 px-3 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-[10px] rounded-sm hover:bg-orange-600 mt-2">
              Retry Camera
            </button>
          </div>
          <button onClick={() => setCameraError(null)} className="w-6 h-6 grid place-items-center text-muted-foreground hover:text-foreground">
            <X size={14} weight="bold" />
          </button>
        </div>
      )}

      {/* Manual entry */}
      <form onSubmit={submitManual} className="card-flat border-border p-4 mb-4">
        <div className="label-tech mb-2">Manual Entry</div>
        <div className="flex gap-2">
          <input data-testid={IDS.scanManualInput} value={manual} onChange={(e) => setManual(e.target.value)}
            placeholder="Paste barcode / QR / SKU / item name"
            className="flex-1 h-11 px-3 border border-border bg-background rounded-sm outline-none focus:ring-2 focus:ring-primary font-mono text-sm" />
          <button data-testid={IDS.scanManualSubmit} type="submit"
            className="btn-hard h-11 px-4 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 flex items-center gap-1">
            <MagnifyingGlass size={16} weight="bold" /> Find
          </button>
        </div>
      </form>

      {recent.length > 0 && (
        <div className="card-flat border-border p-4">
          <div className="label-tech mb-2">Recent Scans</div>
          <div className="flex flex-wrap gap-2">
            {recent.map((r) => (
              <button key={r} onClick={() => handleCode(r)}
                className="px-3 py-1.5 border border-border rounded-sm text-xs font-mono hover:bg-muted">{r}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
